import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Archive, BookOpen, Brain, Check, ChevronRight, Clock3,
  FileText, Fingerprint, HeartHandshake, History, KeyRound, Lightbulb,
  ListTodo, Plus, RefreshCw, ShieldCheck, Sparkles, Target, UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  confirmFinancialMemoryRecord,
  createFinancialMemoryRecord,
  getFinancialMemoryAudit,
  getFinancialMemoryRecords,
  getFinancialMemorySummary,
  refreshFinancialMemoryProposals,
  rejectFinancialMemoryRecord,
} from "@/lib/api";

const SECTIONS = [
  { id: "all", label: "All memory", icon: Brain },
  { id: "family", label: "About family", icon: UserRound },
  { id: "preferences", label: "Preferences", icon: HeartHandshake },
  { id: "investment_policy", label: "Investment policy", icon: ShieldCheck },
  { id: "goals", label: "Goals", icon: Target },
  { id: "decisions", label: "Decisions", icon: Lightbulb },
  { id: "open_loops", label: "Open loops", icon: ListTodo },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "reviews", label: "Reviews", icon: BookOpen },
  { id: "access", label: "Access references", icon: KeyRound },
];

const sectionMeta = (id) => SECTIONS.find((item) => item.id === id) || SECTIONS[0];
const dateLabel = (value) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function FinancialMemoryPage() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [section, setSection] = useState("all");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [auditRecord, setAuditRecord] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [summaryResult, recordsResult] = await Promise.all([
        getFinancialMemorySummary(),
        getFinancialMemoryRecords(),
      ]);
      setSummary(summaryResult.data);
      setRecords(recordsResult.data || []);
    } catch {
      toast.error("Financial Memory could not be loaded");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => records.filter((record) => section === "all" || record.section === section),
    [records, section],
  );
  const proposed = records.filter((record) => record.status === "proposed");
  const confirmed = records.filter((record) => record.status === "confirmed");
  const reviewDue = confirmed.filter((record) => record.next_review_at && new Date(record.next_review_at) <= new Date());

  const decide = async (record, action) => {
    setBusy(record.id);
    try {
      if (action === "confirm") await confirmFinancialMemoryRecord(record.id);
      else await rejectFinancialMemoryRecord(record.id);
      toast.success(action === "confirm" ? "Memory confirmed" : "Proposal rejected");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update this memory");
    } finally {
      setBusy("");
    }
  };

  const refreshProposals = async () => {
    setBusy("refresh");
    try {
      const { data } = await refreshFinancialMemoryProposals();
      toast.success(data.created ? `${data.created} verified proposal${data.created === 1 ? "" : "s"} added` : "No new verified proposals");
      await load();
    } catch {
      toast.error("Could not refresh verified proposals");
    } finally {
      setBusy("");
    }
  };

  const openAudit = async (record) => {
    setAuditRecord(record);
    try {
      const { data } = await getFinancialMemoryAudit(record.id);
      setAudit(data || []);
    } catch {
      setAudit([]);
      toast.error("Audit history could not be loaded");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1720px] mx-auto space-y-5" data-testid="financial-memory-page">
      <section className="relative overflow-hidden rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-cyan-400/[0.08] via-[var(--bg-surface)] to-violet-500/[0.07] p-5 md:p-7">
        <div className="absolute h-56 w-56 rounded-full bg-violet-500/10 blur-3xl -right-16 -top-24 pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-cyan-300 font-semibold">
              <Brain size={14} /> Household knowledge base
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mt-2">Financial Memory</h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)] leading-6">
              The durable context behind your balance sheet, goals and long-range planning—kept separate from the financial ledger.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={refreshProposals} disabled={busy === "refresh"}
              className="h-10 px-3 rounded-lg border border-white/10 text-sm hover:bg-white/5 flex items-center gap-2 disabled:opacity-50">
              <Sparkles size={14} /> {busy === "refresh" ? "Checking…" : "Find verified proposals"}
            </button>
            <button onClick={() => setAdding(true)}
              className="h-10 px-4 rounded-lg bg-cyan-400 text-slate-950 font-semibold text-sm flex items-center gap-2 hover:bg-cyan-300">
              <Plus size={15} /> Add memory
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-4 flex gap-3">
        <KeyRound size={18} className="text-amber-300 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-sm">Secrets stay outside Capital360</div>
          <p className="text-xs leading-5 text-[var(--text-secondary)] mt-1">
            Store passwords, OTPs, PINs, recovery codes and API keys in a password manager. Financial Memory accepts only a safe reference, masked account identifier, login URL, owner and MFA method.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi icon={ShieldCheck} label="Confirmed knowledge" value={confirmed.length} tone="emerald" />
        <Kpi icon={Sparkles} label="Needs confirmation" value={proposed.length} tone="amber" />
        <Kpi icon={ListTodo} label="Open loops" value={summary?.sections?.open_loops || 0} tone="cyan" />
        <Kpi icon={Clock3} label="Review due" value={reviewDue.length} tone="rose" />
      </section>

      {proposed.length > 0 && (
        <section className="rounded-2xl border border-amber-400/20 bg-[var(--bg-surface)] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-semibold">Needs your confirmation</div>
              <h2 className="text-lg font-semibold mt-1">Nothing becomes trusted memory silently</h2>
            </div>
            <span className="text-xs rounded-full border border-amber-400/25 px-3 py-1 text-amber-200">{proposed.length} proposed</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-px bg-white/10">
            {proposed.slice(0, 6).map((record) => (
              <div key={record.id} className="bg-[var(--bg-surface)] p-5">
                <MemoryIdentity record={record} />
                <p className="mt-3 text-sm text-[var(--text-secondary)] leading-6">{record.summary || "No additional context provided."}</p>
                <SourceLine record={record} />
                <div className="mt-4 flex gap-2">
                  <button onClick={() => decide(record, "confirm")} disabled={busy === record.id}
                    className="h-9 px-3 rounded-lg bg-emerald-400/15 border border-emerald-400/30 text-emerald-200 text-sm flex items-center gap-2 hover:bg-emerald-400/20">
                    <Check size={14} /> Confirm
                  </button>
                  <button onClick={() => decide(record, "reject")} disabled={busy === record.id}
                    className="h-9 px-3 rounded-lg border border-white/10 text-[var(--text-secondary)] text-sm flex items-center gap-2 hover:text-white">
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid xl:grid-cols-[250px_1fr] gap-4">
        <aside className="rounded-xl border border-white/10 bg-[var(--bg-surface)] p-2 h-fit xl:sticky xl:top-4">
          {SECTIONS.map((item) => {
            const Icon = item.icon;
            const count = item.id === "all" ? records.length : (summary?.sections?.[item.id] || 0);
            return (
              <button key={item.id} onClick={() => setSection(item.id)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  section === item.id ? "bg-cyan-400/10 text-cyan-200" : "text-[var(--text-secondary)] hover:bg-white/5 hover:text-white"
                }`}>
                <Icon size={15} />
                <span className="flex-1 text-left">{item.label}</span>
                <span className="text-[10px] tabular-nums">{count}</span>
              </button>
            );
          })}
        </aside>

        <div className="rounded-xl border border-white/10 bg-[var(--bg-surface)] overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="font-semibold">{sectionMeta(section).label}</h2>
              <p className="text-xs text-[var(--text-muted)] mt-1">{filtered.length} governed record{filtered.length === 1 ? "" : "s"}</p>
            </div>
            {loading && <RefreshCw size={15} className="animate-spin text-cyan-300" />}
          </div>
          {!loading && filtered.length === 0 ? (
            <EmptyState onAdd={() => setAdding(true)} />
          ) : (
            <div className="divide-y divide-white/10">
              {filtered.map((record) => (
                <button key={record.id} onClick={() => openAudit(record)}
                  className="w-full text-left p-5 hover:bg-white/[0.025] transition-colors grid md:grid-cols-[1fr_auto] gap-4">
                  <div>
                    <MemoryIdentity record={record} />
                    <p className="mt-2 text-sm text-[var(--text-secondary)] line-clamp-2">{record.summary || "No summary"}</p>
                    <SourceLine record={record} />
                  </div>
                  <div className="flex md:flex-col md:items-end justify-between gap-2">
                    <StatusChip status={record.status} />
                    <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
                      <History size={11} /> v{record.version || 1}
                    </span>
                    <ChevronRight size={15} className="text-[var(--text-muted)]" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {adding && <AddMemoryDrawer onClose={() => setAdding(false)} onCreated={() => { setAdding(false); load(); }} />}
      {auditRecord && <AuditDrawer record={auditRecord} audit={audit} onClose={() => { setAuditRecord(null); setAudit([]); }} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const tones = { emerald: "text-emerald-300", amber: "text-amber-300", cyan: "text-cyan-300", rose: "text-rose-300" };
  return (
    <div className="rounded-xl border border-white/10 bg-[var(--bg-surface)] p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)]">{label}</span>
        <Icon size={15} className={tones[tone]} />
      </div>
      <div className={`text-2xl mt-3 font-mono ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function MemoryIdentity({ record }) {
  const meta = sectionMeta(record.section);
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3">
      <span className="h-8 w-8 rounded-lg border border-white/10 bg-white/[0.025] flex items-center justify-center shrink-0">
        <Icon size={15} className="text-cyan-300" />
      </span>
      <div>
        <h3 className="font-semibold leading-5">{record.title}</h3>
        <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-[var(--text-muted)]">
          {meta.label} · {record.owner || "Household"} · {record.sensitivity || "personal"}
        </div>
      </div>
    </div>
  );
}

function SourceLine({ record }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-[var(--text-muted)]">
      <span>Source: {record.source?.label || "Manual"}</span>
      <span>Confidence: {Math.round(Number(record.confidence ?? 1) * 100)}%</span>
      <span>Next review: {dateLabel(record.next_review_at)}</span>
    </div>
  );
}

function StatusChip({ status }) {
  const classes = {
    confirmed: "border-emerald-400/25 bg-emerald-400/10 text-emerald-200",
    proposed: "border-amber-400/25 bg-amber-400/10 text-amber-200",
    rejected: "border-rose-400/25 bg-rose-400/10 text-rose-200",
    superseded: "border-slate-400/25 bg-slate-400/10 text-slate-300",
  };
  return <span className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider ${classes[status] || classes.proposed}`}>{status}</span>;
}

function EmptyState({ onAdd }) {
  return (
    <div className="py-20 px-6 text-center">
      <Archive size={30} className="mx-auto text-[var(--text-muted)]" />
      <h3 className="font-semibold mt-4">No memories in this section</h3>
      <p className="text-sm text-[var(--text-muted)] mt-2">Add a verified fact, preference, decision or review when you are ready.</p>
      <button onClick={onAdd} className="mt-5 text-sm text-cyan-300 hover:text-cyan-200">+ Add a memory</button>
    </div>
  );
}

function AddMemoryDrawer({ onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    section: "family", title: "", summary: "", owner: "Household",
    sensitivity: "personal", next_review_at: "", login_url: "",
    masked_identifier: "", mfa_method: "", password_manager_reference: "",
  });
  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const details = form.section === "access" ? {
        login_url: form.login_url,
        masked_identifier: form.masked_identifier,
        mfa_method: form.mfa_method,
        password_manager_reference: form.password_manager_reference,
      } : {};
      await createFinancialMemoryRecord({
        section: form.section, title: form.title, summary: form.summary,
        owner: form.owner, details, sensitivity: form.sensitivity,
        next_review_at: form.next_review_at || null,
        status: "confirmed", confidence: 1,
        source: { kind: "manual", label: "Confirmed by you" },
      });
      toast.success("Financial memory added");
      onCreated();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save this memory");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer title="Add financial memory" kicker="Confirmed by you" onClose={onClose}>
      <form onSubmit={submit} className="p-5 md:p-7 space-y-5">
        <Field label="Section">
          <select value={form.section} onChange={(event) => set("section", event.target.value)} className="memory-input">
            {SECTIONS.filter((item) => item.id !== "all").map((item) => <option value={item.id} key={item.id}>{item.label}</option>)}
          </select>
        </Field>
        <Field label="Title"><input required minLength={2} value={form.title} onChange={(event) => set("title", event.target.value)} className="memory-input" placeholder="A short, specific title" /></Field>
        <Field label="What should Capital360 remember?"><textarea value={form.summary} onChange={(event) => set("summary", event.target.value)} className="memory-input min-h-32 py-3 resize-y" placeholder="The confirmed context and why it matters" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Owner"><input value={form.owner} onChange={(event) => set("owner", event.target.value)} className="memory-input" /></Field>
          <Field label="Sensitivity">
            <select value={form.sensitivity} onChange={(event) => set("sensitivity", event.target.value)} className="memory-input">
              <option value="standard">Standard</option><option value="personal">Personal</option><option value="sensitive">Sensitive</option>
            </select>
          </Field>
        </div>
        <Field label="Next review date (optional)"><input type="date" value={form.next_review_at} onChange={(event) => set("next_review_at", event.target.value)} className="memory-input" /></Field>

        {form.section === "access" && (
          <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.04] p-4 space-y-4">
            <div className="flex gap-2 text-xs text-amber-200"><Fingerprint size={15} /> Safe access metadata only—never enter the password.</div>
            <Field label="Login URL"><input type="url" value={form.login_url} onChange={(event) => set("login_url", event.target.value)} className="memory-input" placeholder="https://…" /></Field>
            <Field label="Masked member/account ID"><input value={form.masked_identifier} onChange={(event) => set("masked_identifier", event.target.value)} className="memory-input" placeholder="••••1234" /></Field>
            <Field label="MFA method"><input value={form.mfa_method} onChange={(event) => set("mfa_method", event.target.value)} className="memory-input" placeholder="Authenticator app / registered mobile" /></Field>
            <Field label="Password-manager reference"><input value={form.password_manager_reference} onChange={(event) => set("password_manager_reference", event.target.value)} className="memory-input" placeholder="Vault item name only" /></Field>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-white/10 text-sm">Cancel</button>
          <button disabled={saving} className="h-10 px-4 rounded-lg bg-cyan-400 text-slate-950 font-semibold text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Save confirmed memory"}
          </button>
        </div>
      </form>
    </Drawer>
  );
}

function AuditDrawer({ record, audit, onClose }) {
  return (
    <Drawer title={record.title} kicker="Audit & provenance" onClose={onClose}>
      <div className="p-5 md:p-7">
        <MemoryIdentity record={record} />
        <p className="mt-4 text-sm leading-6 text-[var(--text-secondary)]">{record.summary || "No summary"}</p>
        <SourceLine record={record} />
        {Object.keys(record.details || {}).length > 0 && (
          <div className="mt-5 rounded-xl border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)] mb-3">Structured details</div>
            {Object.entries(record.details).filter(([, value]) => value !== "").map(([key, value]) => (
              <div key={key} className="grid grid-cols-[150px_1fr] gap-3 text-xs py-2 border-b border-white/5 last:border-0">
                <span className="text-[var(--text-muted)]">{key.replaceAll("_", " ")}</span><span className="break-all">{String(value ?? "—")}</span>
              </div>
            ))}
          </div>
        )}
        <h3 className="font-semibold mt-7">Audit trail</h3>
        <div className="mt-3 space-y-2">
          {audit.length === 0 && <p className="text-sm text-[var(--text-muted)]">No audit events found.</p>}
          {audit.map((event) => (
            <div key={event.id} className="rounded-lg border border-white/10 p-3 flex gap-3">
              <History size={14} className="text-cyan-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm capitalize">{String(event.action || "").replaceAll("_", " ")}</div>
                <div className="text-[10px] text-[var(--text-muted)] mt-1">{dateLabel(event.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}

function Drawer({ title, kicker, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onMouseDown={onClose}>
      <aside className="h-full w-full max-w-2xl overflow-y-auto bg-[var(--bg-base)] border-l border-white/10 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[var(--bg-base)]/95 backdrop-blur border-b border-white/10 px-5 md:px-7 py-5 flex items-start justify-between">
          <div><div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300">{kicker}</div><h2 className="text-xl font-semibold mt-1">{title}</h2></div>
          <button onClick={onClose} className="h-9 w-9 rounded-lg border border-white/10 flex items-center justify-center hover:bg-white/5"><X size={17} /></button>
        </div>
        {children}
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] uppercase tracking-[0.14em] text-[var(--text-muted)] mb-2">{label}</span>{children}</label>;
}
